/* Matching sales to the shares they actually sold.
 *
 * The book carries an average cost, which is the right figure for reading a
 * position and the wrong one for a tax return: a return wants the cost of the
 * shares that were sold, and where several parcels were bought at different
 * prices the convention is that the earliest go first.
 *
 * The two answers differ, sometimes by a lot. Neither is an approximation of
 * the other — they answer different questions — so both are reported and each
 * says which it is.
 *
 * Commission belongs in both halves and was in neither. What a purchase cost
 * you includes what the broker took to make it, and what a sale brought in is
 * net of what it took to close it — that is what a return asks for, and it is
 * already how `avgCost` and the book's own realised figure are worked out (see
 * `state/commission.ts`). Leaving fees out here understated the cost and
 * overstated the proceeds, so the one screen where a number turns into tax owed
 * was the one reporting a gain larger than the trade made. A buy's fee is
 * carried per share, so a parcel sold in pieces takes its share of it.
 *
 * The part that matters most here is what happens when the record is
 * incomplete. This book was seeded with positions and almost no trades, so
 * there are sales with no matching purchase anywhere in it. Those are not
 * guessed at and not counted as costing nothing, which would report the entire
 * proceeds as profit. They are listed, by ticker and size, as the gap they
 * are. */

import type { ShareTransfer, Trade } from '../types';
import { costWithFee } from './commission';

export interface FifoSale {
  tradeId: string;
  ticker: string;
  account: string;
  at: string;
  shares: number;
  proceeds: number;
  cost: number;
  gain: number;
}

export interface FifoResult {
  sales: FifoSale[];
  /** Sales the log has no purchase for. Reported rather than costed at zero. */
  unmatched: Array<{ ticker: string; account: string; at: string; shares: number }>;
}

/* A parcel carries its own acquisition date, because a transfer has to put it
   back in date order at the far end rather than on the end of the queue. */
interface Lot { shares: number; price: number; at: string }

const EPSILON = 1e-9;

/* Shares moved between accounts, replayed alongside the trades.
 *
 * The queues are keyed by account, so without this a transfer leaves the
 * destination empty and every later sale there falls into `unmatched` — on the
 * one screen where a number becomes tax owed — while the source queue keeps
 * parcels for shares that are no longer in it and prices the next sale there
 * against them.
 *
 * The parcels move at their **own** per-share cost, not at the lot's blended
 * average: FIFO's whole claim is that the earliest shares went first, and a
 * blended figure would answer a different question. They are spliced into the
 * destination in acquisition-date order rather than appended, or a parcel
 * bought in 2019 would sit behind shares bought at the destination in 2024 and
 * the next sale would take the wrong cost. */
type Event =
  | { kind: 'trade'; at: string; trade: Trade }
  | { kind: 'transfer'; at: string; transfer: ShareTransfer };

function applyTransfer(lots: Map<string, Lot[]>, x: ShareTransfer): void {
  const from = `${x.fromAccount}|${x.ticker}`;
  const to = `${x.toAccount}|${x.ticker}`;
  const source = lots.get(from) ?? [];
  let left = x.shares;
  const moving: Lot[] = [];

  while (left > EPSILON && source.length > 0) {
    const parcel = source[0]!;
    const take = Math.min(left, parcel.shares);
    moving.push({ shares: take, price: parcel.price, at: parcel.at });
    parcel.shares -= take;
    left -= take;
    if (parcel.shares <= EPSILON) source.shift();
  }
  lots.set(from, source);

  /* Shares the log has no purchase for move nothing extra and report nothing
     extra: a later sale of them falling into `unmatched` is the honest answer
     this file already gives for an incomplete record. */
  const dest = lots.get(to) ?? [];
  for (const parcel of moving) {
    const at = dest.findIndex((p) => p.at.localeCompare(parcel.at) > 0);
    if (at === -1) dest.push(parcel);
    else dest.splice(at, 0, parcel);
  }
  lots.set(to, dest);
}

export function fifoRealised(trades: Trade[], transfers: ShareTransfer[] = []): FifoResult {
  const lots = new Map<string, Lot[]>();
  const sales: FifoSale[] = [];
  const unmatched: FifoResult['unmatched'] = [];

  const key = (t: Trade) => `${t.account}|${t.ticker}`;
  // Order is the whole point: earliest bought, earliest sold.
  const events: Event[] = [
    ...trades.map((t) => ({ kind: 'trade' as const, at: t.at, trade: t })),
    ...transfers.map((x) => ({ kind: 'transfer' as const, at: x.at, transfer: x })),
  ].sort((a, b) => a.at.localeCompare(b.at));

  for (const event of events) {
    if (event.kind === 'transfer') {
      applyTransfer(lots, event.transfer);
      continue;
    }
    const t = event.trade;
    const k = key(t);
    const queue = lots.get(k) ?? [];

    if (t.side === 'buy') {
      // Per share, fee included, so taking part of a parcel takes part of it.
      queue.push({ shares: t.shares, price: costWithFee(t.shares, t.price, t.fee ?? 0), at: t.at });
      lots.set(k, queue);
      continue;
    }

    let left = t.shares;
    let cost = 0;
    while (left > EPSILON && queue.length > 0) {
      const lot = queue[0]!;
      const take = Math.min(left, lot.shares);
      cost += take * lot.price;
      lot.shares -= take;
      left -= take;
      if (lot.shares <= EPSILON) queue.shift();
    }
    lots.set(k, queue);

    if (left > EPSILON) {
      unmatched.push({ ticker: t.ticker, account: t.account, at: t.at, shares: left });
    }

    const matched = t.shares - left;
    if (matched > EPSILON) {
      /* The exit fee comes off the proceeds, and only the part of it that
         belongs to the shares this sale could be matched to — the rest belongs
         with the unmatched remainder, which is reported rather than costed. */
      const exitFee = Math.abs(t.fee ?? 0) * (t.shares > 0 ? matched / t.shares : 0);
      const proceeds = matched * t.price - exitFee;
      sales.push({
        tradeId: t.id,
        ticker: t.ticker,
        account: t.account,
        at: t.at,
        shares: matched,
        proceeds,
        cost,
        gain: proceeds - cost,
      });
    }
  }

  return { sales, unmatched };
}
